import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LayoutDashboard, FileText, Users, Clock, Layers, MapPin, ShieldCheck, UserCog, Megaphone } from 'lucide-react';

const Navbar = ({ role, username, onLogout, permissions = [] }) => {
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isManagementOpen, setIsManagementOpen] = useState(false);

    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    // Filter permissions for the current role
    const myPermissions = permissions.filter(p => p.role.toLowerCase() === role.toLowerCase());

    const isVisible = (menuKey) => {
        const perm = myPermissions.find(p => p.menu_key === menuKey);
        return perm ? !!perm.is_visible : false;
    };

    // Helper to check if a path is active
    const isActive = (path) => location.pathname === path;

    const isAdmin = role === 'admin' || role === 'Admin';

    const handleActualLogout = () => {
        setShowLogoutConfirm(false);
        onLogout();
    };

    return (
        <>
            <header className="bg-white text-slate-800 sticky top-0 z-[200] border-b border-slate-200 shadow-sm mb-0">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo Section */}
                        <div className="flex items-center gap-3 shrink-0">
                            <Link to="/" className="flex items-center gap-2 group">
                                <div className="bg-gradient-to-tr from-blue-600 to-indigo-600 text-white p-2 rounded-xl shadow-lg shadow-blue-500/25 group-hover:scale-105 transition-all duration-300 shrink-0">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <div className="flex flex-col lg:hidden xl:flex">
                                    <h1 className="text-sm md:text-base font-black bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent truncate tracking-tight">
                                        <span className="text-blue-600">Parking Audit System</span>
                                    </h1>
                                </div>
                            </Link>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center gap-1 xl:gap-2">
                            <div className="flex items-center bg-slate-100/50 p-1 rounded-xl gap-0.5 border border-slate-200/50">
                                {isVisible('dashboard') && (
                                    <Link
                                        to="/"
                                        className={`flex items-center gap-1.5 px-2.5 xl:px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all ${isActive('/')
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                                            }`}
                                    >
                                        <LayoutDashboard className="w-4 h-4" />
                                        <span className="hidden xl:inline">Dashboard</span>
                                    </Link>
                                )}
                                {isVisible('reports') && (
                                    <Link
                                        to="/reports"
                                        className={`flex items-center gap-1.5 px-2.5 xl:px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all ${isActive('/reports')
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                                            }`}
                                    >
                                        <FileText className="w-4 h-4" />
                                        <span className="hidden xl:inline">Report</span>
                                    </Link>
                                )}
                                {isVisible('residents') && (
                                    <Link
                                        to="/residents"
                                        className={`flex items-center gap-1.5 px-2.5 xl:px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all ${isActive('/residents')
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                                            }`}
                                    >
                                        <Users className="w-4 h-4" />
                                        <span className="hidden xl:inline">Resident</span>
                                    </Link>
                                )}

                                {/* Management Dropdown */}
                                {(isVisible('rounds') || isVisible('zones') || isVisible('slots') || isVisible('users') || isVisible('roles') || isVisible('announcements')) && (
                                    <div className="relative group">
                                        <button
                                            onMouseEnter={() => setIsManagementOpen(true)}
                                            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all ${['/rounds', '/zones', '/slots', '/users', '/roles', '/announcements'].includes(location.pathname)
                                                ? 'bg-white text-blue-600 shadow-sm'
                                                : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                                                }`}
                                        >
                                            <ShieldCheck className="w-4 h-4" />
                                            <span className="hidden xl:inline">Manage</span>
                                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isManagementOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {/* Dropdown Menu */}
                                        <div
                                            onMouseLeave={() => setIsManagementOpen(false)}
                                            className={`absolute top-full right-0 md:left-0 mt-2 w-32 bg-white border border-slate-200 rounded-2xl shadow-2xl py-2 z-50 transition-all duration-200 origin-top-left ${isManagementOpen ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
                                                }`}
                                        >
                                            {isVisible('announcements') && (
                                                <Link
                                                    to="/announcements"
                                                    className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] font-bold transition-colors ${isActive('/announcements') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive('/announcements') ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                                        <Megaphone className="w-3.5 h-3.5" />
                                                    </div>
                                                    Announce
                                                </Link>
                                            )}
                                            {isVisible('rounds') && (
                                                <Link
                                                    to="/rounds"
                                                    className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] font-bold transition-colors ${isActive('/rounds') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive('/rounds') ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                                        <Clock className="w-3.5 h-3.5" />
                                                    </div>
                                                    Round
                                                </Link>
                                            )}
                                            {isVisible('zones') && (
                                                <Link
                                                    to="/zones"
                                                    className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] font-bold transition-colors ${isActive('/zones') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive('/zones') ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                                        <Layers className="w-3.5 h-3.5" />
                                                    </div>
                                                    Zone
                                                </Link>
                                            )}
                                            {isVisible('slots') && (
                                                <Link
                                                    to="/slots"
                                                    className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] font-bold transition-colors ${isActive('/slots') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive('/slots') ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                                        <MapPin className="w-3.5 h-3.5" />
                                                    </div>
                                                    Slot
                                                </Link>
                                            )}
                                            {isVisible('users') && (
                                                <Link
                                                    to="/users"
                                                    className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] font-bold transition-colors ${isActive('/users') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive('/users') ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                                        <UserCog className="w-3.5 h-3.5" />
                                                    </div>
                                                    User
                                                </Link>
                                            )}
                                            {isVisible('roles') && (
                                                <Link
                                                    to="/roles"
                                                    className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] font-bold transition-colors ${isActive('/roles') ? 'text-blue-600 bg-blue-50/50' : 'text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive('/roles') ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                                                        <ShieldCheck className="w-3.5 h-3.5" />
                                                    </div>
                                                    Role
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* User Profile */}
                            <div className="flex items-center gap-2.5 pl-4 border-l border-slate-200 ml-2">
                                <div className="relative">
                                    <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-slate-100 to-slate-200 flex items-center justify-center text-slate-600 font-black text-sm border border-slate-200 shadow-sm">
                                        {username ? username.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                </div>
                                <div className="flex flex-col items-start hidden xl:flex">
                                    <span className="text-[13px] font-black text-slate-700 leading-tight">{username || 'User'}</span>
                                    <div className="flex items-center gap-1.5">
                                        <div className={`w-1.5 h-1.5 rounded-full ${isAdmin ? 'bg-indigo-500 animate-pulse' : 'bg-emerald-500'}`} />
                                        <span className="text-[10px] font-black tracking-widest text-slate-400">
                                            {role}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowLogoutConfirm(true)}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all border border-slate-200 hover:border-rose-100 bg-white shadow-sm active:scale-95 ml-1"
                                    title="ออกจากระบบ"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                {isMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-2 space-y-1 animate-in slide-in-from-top-2">
                        <div className="flex items-center justify-between py-3 border-b border-slate-100 mb-2">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-lg">
                                    {username ? username.charAt(0).toUpperCase() : 'U'}
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-bold text-slate-800">{username || 'User'}</span>
                                    <span className="text-xs text-slate-500 capitalize">{role}</span>
                                </div>
                            </div>
                        </div>

                        {isVisible('dashboard') && (
                            <Link
                                to="/"
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <LayoutDashboard className="w-5 h-5" />
                                Dashboard
                            </Link>
                        )}
                        {isVisible('reports') && (
                            <Link
                                to="/reports"
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/reports') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <FileText className="w-5 h-5" />
                                Reports
                            </Link>
                        )}
                        {isVisible('residents') && (
                            <Link
                                to="/residents"
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/residents') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <Users className="w-5 h-5" />
                                Resident
                            </Link>
                        )}

                        {/* Mobile Management Section */}
                        {(isVisible('rounds') || isVisible('zones') || isVisible('slots') || isVisible('users') || isVisible('roles') || isVisible('announcements')) && (
                            <div className="py-2">
                                <div className="px-4 py-2 text-[10px] font-black text-slate-400">Manage</div>
                                <div className="space-y-1 mt-1">
                                    {isVisible('announcements') && (
                                        <Link
                                            to="/announcements"
                                            onClick={() => setIsMenuOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/announcements') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            <Megaphone className="w-4 h-4" />
                                            Announcement
                                        </Link>
                                    )}
                                    {isVisible('rounds') && (
                                        <Link
                                            to="/rounds"
                                            onClick={() => setIsMenuOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/rounds') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            <Clock className="w-4 h-4" />
                                            Round
                                        </Link>
                                    )}
                                    {isVisible('zones') && (
                                        <Link
                                            to="/zones"
                                            onClick={() => setIsMenuOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/zones') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            <Layers className="w-4 h-4" />
                                            Zone
                                        </Link>
                                    )}
                                    {isVisible('slots') && (
                                        <Link
                                            to="/slots"
                                            onClick={() => setIsMenuOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/slots') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            <MapPin className="w-4 h-4" />
                                            Slot
                                        </Link>
                                    )}
                                    {isVisible('users') && (
                                        <Link
                                            to="/users"
                                            onClick={() => setIsMenuOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/users') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            <UserCog className="w-4 h-4" />
                                            User
                                        </Link>
                                    )}
                                    {isVisible('roles') && (
                                        <Link
                                            to="/roles"
                                            onClick={() => setIsMenuOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold ${isActive('/roles') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            <ShieldCheck className="w-4 h-4" />
                                            Role
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => {
                                setShowLogoutConfirm(true);
                                setIsMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 rounded-xl text-sm font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-2 mt-2"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Logout
                        </button>
                    </div>
                )}
            </header>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-200"
                        onClick={() => setShowLogoutConfirm(false)}
                    />
                    <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-[320px] overflow-hidden animate-in zoom-in-95 fade-in duration-200 border border-slate-100">
                        <div className="p-6 text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-rose-500">
                                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-black text-slate-800 mb-2">ออกจากระบบ</h3>
                            <p className="text-[13px] text-slate-500 font-medium leading-relaxed">
                                คุณแน่ใจหรือไม่ว่าต้องการ<br />ออกจากระบบ?
                            </p>
                        </div>
                        <div className="flex p-4 gap-3 bg-slate-50/50">
                            <button
                                onClick={() => setShowLogoutConfirm(false)}
                                className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-all active:scale-95 shadow-sm"
                            >
                                ยกเลิก
                            </button>
                            <button
                                onClick={handleActualLogout}
                                className="flex-1 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white bg-rose-500 hover:bg-rose-600 shadow-lg shadow-rose-200 transition-all active:scale-95"
                            >
                                ออกจากระบบ
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;
